import {Component, FontAwesomeIcon} from 'substance'
import moment from 'moment'
import jxon from '@infomaker/im-jxon'
import {NilUUID} from '../../../lib/utils/NilUUID'
import './scss/extensiontabs.scss'

/**
 * @typedef UIDialogImage.props
 * @property {Node} node - The Image's Substance Node
 * @property {object} newsItem - Newsitem representation of image
 * @property {string} url - Render url for image
 * @property {boolean} disablebylinesearch - Disable byline search functionality
 * @property {boolean} [focusOnRender=false] - Whether or not to focus on the first input field after render. Default is false
 */

/**
 * @typedef UIDialogImage.state
 * @property {string} tab -  `"main"`, `"active"`, or `"extended"`
 */

/**
 * A dialog image component
 *
 * Used to display and edit image metadata in a dialog window.
 *
 * @name UIDialogImage
 * @class
 *
 * @example
 * import {UIDialogImage} from 'writer'
 *
 * render($$) {
 *     api.ui.showDialog(
 *          UIDialogImage,
 *          {
 *              node: imageNode,
 *              url: imageNode.getUrl(),
 *              newsItem: imageNewsItem,
 *              disablebylinesearch: false,
 *              focusOnRender: true
 *          },
 *          {
 *              title: this.getLabel('Image archive information'),
 *              global: true,
 *              primary: this.getLabel('Save'),
 *              secondary: this.getLabel('Cancel'),
 *              cssClass: 'np-image-dialog hide-overflow',
 *              focusPrimary: false
 *          }
 *     )
 * }
 *
 * @param {UIDialogImage.props} props
 * @property {UIDialogImage.props} props
 * @property {UIDialogImage.state} state
 */
class DialogImageComponent extends Component {
    constructor(...args) {
        super(...args)

        this.authors = []
        this.data = null
        this.parsing = false

        this.addAuthor = this.addAuthor.bind(this)
    }

    getInitialState() {
        return {
            tab: 'main'
        }
    }

    dispose() {
        Component.prototype.dispose.call(this)
    }

    didMount() {
        if(this.props.focusOnRender) {
            this.refs.infoCaption.getNativeElement().focus()
        }
    }

    render($$) {
        var el = $$('div')

        if (this.parsing === false) {
            this.parseData()
        }

        var p = $$('p')

        if (this.data.width) {
            p.append($$('span').addClass('dialog-image-width').append(
                this.data.width + ' px'
            ))
        }

        if (this.data.height) {
            p.append($$('span').addClass('dialog-image-height').append(
                this.data.height + ' px'
            ))
        }

        var dt = new Date(this.data.photoDateTime)
        p.append([
            $$('strong').append(this.getLabel('Photo date')),
            $$('span').addClass('dialog-image-photo-date').append(
                $$('input').attr('id', 'dialog-image-photo-date').attr('type', 'date').val(
                    !isNaN(dt.getTime()) ? dt.toLocaleDateString() : ''
                ).on('change', () => {
                    if(!isNaN(dt.getTime())) {
                        this.refs.photoTime.val('00:00')
                    }

                    this.syncPhotoDate()
                })
            ),
            $$('span').addClass('dialog-image-photo-time').append(
                $$('input').attr('id', 'dialog-image-photo-time').attr('type', 'time').val(
                    !isNaN(dt.getTime()) ? dt.toLocaleTimeString() : ''
                ).on('change', this.syncPhotoDate.bind(this)).ref('photoTime')
            )
        ])

        dt = new Date(this.data.firstCreated)
        if (!isNaN(dt.getTime())) {
            p.append([
                $$('strong').append(this.getLabel('Imported')),
                $$('span').addClass('dialog-image-create-date').append(
                    dt.toLocaleDateString()
                ),
                $$('span').addClass('dialog-image-create-time').append(
                    dt.toLocaleTimeString()
                )
            ])
        }

        p.append(
            this.renderStatus($$, this.data.pubStatus)
        )

        el.append(
            $$('div').addClass('image-preview').append(
                $$('img').attr('src', this.data.url)
            ).append(p)
        )

        const form = $$('form')
        const mainMetadata = $$('div').append(
            $$('fieldset').addClass('form-group').append([
                $$('div').addClass('form-group').append(
                    $$('textarea')
                        .ref('infoCaption')
                        .on('change', (evt) => {
                            this.data.text = evt.target.value
                        })
                        .attr('id', 'dialog-image-info-caption')
                        .addClass('form-control')
                        .attr({
                            rows: 3,
                            placeholder: this.getLabel('Image description')
                        })
                        .append(
                            this.data.text
                        )
                ),

                this.renderAuthorContainer($$),

                $$('div').addClass('form-group flexible-label').append(
                    $$('input')
                        .on('change', (evt) => {
                            this.data.credit = evt.target.value
                        })
                        .addClass('form-control')
                        .attr({
                            required: 'required',
                            type: 'text',
                            id: 'dialog-image-info-credit',
                            placeholder: this.getLabel('Credit')
                        })
                        .val(this.data.credit),
                    $$('label')
                        .attr('for', 'dialog-image-info-credit')
                        .append(
                            this.getLabel('Credit')
                        )
                ),

                $$('div').addClass('form-group flexible-label').append(
                    $$('input')
                        .on('change', (evt) => {
                            this.data.instructions = evt.target.value
                        })
                        .addClass('form-control')
                        .attr({
                            required: 'required',
                            type: 'text',
                            id: 'dialog-image-info-instructions',
                            placeholder: this.getLabel('Instructions')
                        })
                        .val(this.data.instructions),
                    $$('label')
                        .attr('for', 'dialog-image-info-instructions')
                        .append(
                            this.getLabel('Instructions')
                        )
                ),

                $$('div').addClass('form-group flexible-label').append(
                    $$('input')
                        .on('change', (evt) => {
                            this.data.objectName = evt.target.value
                        })
                        .addClass('form-control')
                        .attr({
                            required: 'required',
                            type: 'text',
                            id: 'dialog-image-info-objectname',
                            placeholder: this.getLabel('Object name')
                        })
                        .val(this.data.objectName),
                    $$('label')
                        .attr('for', 'dialog-image-info-objectname')
                        .append(
                            this.getLabel('Object name')
                        )
                )
            ])
        )

        const extensionsEl = this.renderExtensions($$, mainMetadata)
        if (extensionsEl) {
            form.append(extensionsEl)
        }
        else {
            form.append(mainMetadata)
        }

        el.addClass('dialog-image-info')
        el.append(form)

        return el
    }

    renderExtensions($$, mainEl) {
        const extensionEls = []
        const extensionModules = this.context.api.getPluginModulesForTarget('se.infomaker.npwriter.dialogimage')

        if (!Array.isArray(extensionModules) || extensionModules.length === 0) {
            return null
        }

        extensionModules.forEach(extension => {
            extensionEls.push($$(extension, {
                properties: this.data.extended || {},
                onChange: (id, value) => {
                    if (!this.data.extended) {
                        this.data.extended = {}
                    }
                    this.data.extended[id] = value
                }
            }))
        })

        return $$('fieldset').addClass('form-group').append(
            $$('div').addClass('form-group').append(
                this.renderExtensionsTab($$, mainEl, extensionEls)
            )
        )
    }

    renderExtensionsTab($$, mainEl, extensionEls) {
        const tabsEl = $$('div').addClass('ui-extensiontabs').append([
            $$('div').append([
                $$('a').append('Image meta data')
                    .addClass(this.state.tab === 'main' ? 'active' : '')
                    .on('click', () => {
                        this.extendState({tab: 'main'})
                    }),
                $$('a').append('Extended meta data')
                    .addClass(this.state.tab === 'extended' ? 'active' : '')
                    .on('click', () => {
                        this.extendState({tab: 'extended'})
                    })
            ])
        ])

        if (this.state.tab === 'main') {
            tabsEl.append(
                $$('div').append(mainEl)
            )
        }
        else {
            tabsEl.append(
                $$('div').append(extensionEls)
            )
        }

        return tabsEl
    }

    renderAuthorContainer($$) {
        const el = $$('div')
            .ref('authorContainer')
            .addClass('authors dialog-image-info clearfix')

        let searchComponent
        const placeholderText = this.getLabel('Add creator')

        if (this.props.disablebylinesearch) {
            const AuthorAddComponent = this.context.configurator.getComponent('UIFormAdd')

            searchComponent = $$(AuthorAddComponent, {
                existingItems: this.authors,
                onSelect: this.addAuthor,
                onCreate: (authorItem) => {
                    this.createAuthor(authorItem, false)
                },
                createAllowed: true,
                placeholderText
            }).ref('FormSearchComponent')

        } else {
            const AuthorSearchComponent = this.context.configurator.getComponent('UIFormSearch')
            searchComponent = $$(AuthorSearchComponent, {
                existingItems: this.authors,
                conceptService: this.context.services.conceptService,
                onSelect: this.addAuthor,
                onCreate: (authorItem) => {
                    this.createAuthor(authorItem, false)
                },
                createAllowed: true,
                placeholderText
            }).ref('FormSearchComponent')
        }

        return el.append([
            searchComponent,
            this.renderAuthors($$)
        ])
    }


    renderAuthors($$) {
        const authorList = $$('ul')
            .addClass('dialog-image-authorlist')
            .attr('contenteditable', false)

        for(let n = 0; n < this.authors.length; n++) {
            const authorItem = this.renderAuthor($$, this.authors[n])
            if (authorItem) {
                authorList.append(authorItem)
            }
        }

        return $$('div')
            .addClass('dialog-image-authors')
            .append(
                authorList
            )
    }

    /**
     * @param $$
     * @param author
     *
     * @returns {*}
     * @private
     */
    renderAuthor($$, author) {

        /**
         * TODO: Implement byline search
         */

        const avatar = this._renderAvatar($$, author)
        let ref

        if (author.uuid && !NilUUID.isNilUUID(author.uuid)) {
            ref = 'item-' + author.uuid
        }
        else {
            ref = 'item-' + author.name
        }

        return $$('li').append(
            $$('div').append([
                avatar,
                $$('div').append([
                    $$('strong').append(author.name)
                ]),
                $$('span').append(
                    $$('a').append(
                        $$(FontAwesomeIcon, {icon: 'fa-times'})
                    )
                        .attr('title', this.getLabel('Remove'))
                        .on('click', function() {
                            this.removeAuthor(author)
                        }.bind(this))
                )
            ])
        ).ref(ref)
    }

    /**
     * @param $$
     * @param author
     *
     * @returns {*}
     *
     * @private
     */
    _renderAvatar($$, author) {
        const Avatar = this.context.configurator.getComponent('UIAvatar')
        const source = {
            avatarSource: 'gravatar',
            avatarId: author.email || author.data.email
        }

        return $$(Avatar, source)
    }

    renderStatus($$, pubStatus) {
        var txtStatus = ''

        switch(pubStatus) {
            case 'stat:withheld':
                txtStatus = 'Not published'
                break

            case 'imext:done':
                txtStatus = 'Pending approval'
                break

            case 'stat:usable':
                txtStatus = 'Published'
                break

            case 'stat:canceled':
                txtStatus = 'Unpublished'
                break

            default:
                txtStatus = 'Not published'
        }

        return $$('div').append(
            $$('strong')
                .addClass('dialog-image-status')
                .append(
                    this.getLabel(txtStatus)
                )
        )
    }

    addAuthor(authorItem) {
        this.fetchAuthor(authorItem.uuid, function(author) {
            this.authors.push(author)
            this.rerender()
        }.bind(this), true)
    }

    createAuthor(authorItem, skipRender) {
        var name = authorItem.inputValue ? authorItem.inputValue : authorItem.name
        var author = {
            uuid: NilUUID.getNilUUID(),
            name: name,
            data: {
                email: ''
            }
        }

        this.authors.push(author)

        if (!skipRender) {
            this.rerender()
        }
    }

    removeAuthor(author) {
        let ref,
            id

        if (author.uuid && !NilUUID.isNilUUID(author.uuid)) {
            ref = 'item-' + author.uuid
            id = author.uuid
        }
        else {
            ref = 'item-' + author.name
            id = author.name
        }


        delete this.refs[ref]
        for(let n = 0; n < this.authors.length; n++) {
            let cmpid
            let author = this.authors[n]
            if (author.uuid && !NilUUID.isNilUUID(author.uuid)) {
                cmpid = author.uuid
            }
            else {
                cmpid = author.name
            }
            if (cmpid === id) {
                this.authors.splice(n, 1)
                break
            }
        }

        this.rerender()
    }

    syncPhotoDate() {
        var photoDate = document.getElementById('dialog-image-photo-date').value,
            photoTime = document.getElementById('dialog-image-photo-time').value,
            dt = new moment(photoDate + 'T' + photoTime)

        if (typeof dt === 'string') {
            return null
        }

        try {
            this.data.photoDateTime = moment.utc(dt).format()
        }
        catch(ex) {
            return null
        }

        return this.data.photoDateTime
    }

    onClose(status) {
        if (status !== 'save') {
            return
        }

        const dom = this.props.newsItem
        var xmlSerializer = new XMLSerializer(), /* jshint ignore:line */
            text = dom.querySelector('contentMeta > metadata > object > data > text'),
            credit = dom.querySelector('contentMeta > metadata > object > data > credit'),
            objectName = dom.querySelector('contentMeta > metadata > object > data > objectName'),
            instructions = dom.querySelector('contentMeta > metadata > object > data > instructions'),
            photoDateTime = dom.querySelector('contentMeta > metadata > object > data > photoDateTime'),
            itemMeta = dom.querySelectorAll('itemMeta'),
            links = dom.querySelectorAll('itemMeta > links'),
            authors = dom.querySelectorAll('itemMeta > links > link[rel="author"]')

        if (!links || !links.length) {
            links = dom.createElement('links')
            itemMeta[0].appendChild(links)
        }
        else {
            links = links[0]
        }

        var data = dom.querySelector('contentMeta > metadata > object > data')
        this.addOrUpdateSimpleNode(dom, data, text, 'text', this.data.text) // document.getElementById('dialog-image-info-caption').value)
        this.addOrUpdateSimpleNode(dom, data, credit, 'credit', this.data.credit) // document.getElementById('dialog-image-info-credit').value)
        this.addOrUpdateSimpleNode(dom, data, instructions, 'instructions', this.data.instructions) // document.getElementById('dialog-image-info-instructions').value)
        this.addOrUpdateSimpleNode(dom, data, objectName, 'objectName', this.data.objectName) // document.getElementById('dialog-image-info-objectname').value)

        this.addOrUpdateExtendedData(dom, data)

        var dtString = this.syncPhotoDate()
        if (null === dtString) {
            if (photoDateTime) {
                data.removeChild(photoDateTime)
            }
        }
        else {
            this.addOrUpdateSimpleNode(dom, data, photoDateTime, 'photoDateTime', dtString)
        }

        for(var x = 0; x < authors.length; x++) {
            links.removeChild(authors[x])
        }

        for(var n = 0; n < this.authors.length; n++) {
            var link = dom.createElement('link')
            link.setAttribute('rel', 'author')
            link.setAttribute('title', this.authors[n].name)
            link.setAttribute('type', 'x-im/author')

            if (this.authors[n].uuid && !NilUUID.isNilUUID(this.authors[n].uuid)) {

                link.setAttribute('uuid', this.authors[n].uuid)
            }
            else {
                link.setAttribute('uuid', NilUUID.getNilUUID())
            }

            links.appendChild(link)
        }

        var xml = xmlSerializer.serializeToString(dom)
        this.saveImageData(
            this.data.uuid,
            xml
        )
    }

    addOrUpdateExtendedData(dom, data) {
        let extended = false
        if (!this.data.extended) {
            return
        }

        let extEl = dom.querySelector('contentMeta > metadata > object > data > extended')
        if (!extEl) {
            extEl = dom.createElement('extended')
        }

        for(var key in this.data.extended) {
            extended = true
            this.addOrUpdateSimpleNode(
                dom,
                extEl,
                null,
                key,
                this.data.extended[key]
            )
        }

        if (extended) {
            data.appendChild(extEl)
        }
    }

    addOrUpdateSimpleNode(dom, parent, node, name, value) {
        if (node) {
            node.textContent = value
        }
        else {
            node = dom.createElementNS(parent.namespaceURI, name)
            node.textContent = value
            parent.appendChild(node)
        }
    }

    parseData() {
        this.parsing = true

        const dom = this.props.newsItem,
            newsItem = dom.querySelector('newsItem'),
            authors = dom.querySelectorAll('itemMeta > links > link[rel="author"]'),
            text = dom.querySelector('contentMeta > metadata > object > data > text'),
            credit = dom.querySelector('contentMeta > metadata > object > data > credit'),
            objectName = dom.querySelector('contentMeta > metadata > object > data > objectName'),
            instructions = dom.querySelector('contentMeta > metadata > object > data > instructions'),
            photoDateTime = dom.querySelector('contentMeta > metadata > object > data > photoDateTime'),
            pubStatus = dom.querySelector('itemMeta > pubStatus'),
            width = dom.querySelector('contentMeta > metadata > object > data > width'),
            height = dom.querySelector('contentMeta > metadata > object > data > height'),
            firstCreated = dom.querySelector('itemMeta > firstCreated')

        const extendedEls = dom.querySelector('contentMeta > metadata > object > data > extended')
        const extended = {}
        if (extendedEls && extendedEls.children && extendedEls.children.length) {
            for(var i = 0; i < extendedEls.children.length; i++) {
                extended[extendedEls.children[i].tagName] = extendedEls.children[i].textContent
            }
        }

        if (authors && authors.length > 0) {
            for(var n = 0; n < authors.length; n++) {
                var author = {
                    uuid: authors[n].getAttribute('uuid'),
                    name: authors[n].getAttribute('title'),
                    data: {
                        email: ''
                    }
                }

                if (!author.name) {
                    author.name = this.getLabel('Unknown name')
                }

                if (author.uuid === null) {
                    author.uuid = NilUUID.getNilUUID()
                }

                if (NilUUID.isNilUUID(author.uuid)) {
                    this.createAuthor(author, true)
                }
                else {
                    this.addAuthor(author)
                }
            }
        }

        this.data = {
            uuid: newsItem.getAttribute('guid'),
            url: this.props.url,
            text: text ? text.textContent : '',
            credit: credit ? credit.textContent : '',
            objectName: objectName ? objectName.textContent : '',
            instructions: instructions ? instructions.textContent : '',
            pubStatus: pubStatus.getAttribute('qcode'),
            firstCreated: firstCreated ? firstCreated.textContent : '',
            photoDateTime: photoDateTime ? photoDateTime.textContent : '',
            width: width ? width.textContent : null,
            height: height ? height.textContent : null,
            authors: this.authors,
            extended: extended
        }
    }


    fetchAuthor(uuid, cbDone, fetchOnly) {
        this.context.api.router.getNewsItem(uuid, 'x-im/author')
            .then((data) => {
                var conceptXML = data.querySelector('concept')
                var jsonFormat = jxon.build(conceptXML)
                var authorLinks = []

                try {
                    authorLinks = jxon.build(data.querySelector('itemMeta links'))
                } catch(e) {
                }

                var author = {
                    uuid: uuid,
                    name: jsonFormat.name,
                    data: jsonFormat.metadata.object.data,
                    json: jsonFormat,
                    links: authorLinks
                }

                if (fetchOnly === true) {
                    cbDone(author)
                }
                else {
                    // Check that we don't already have this
                    for(var n = 0; n < this.authors.length; n++) {
                        if (this.authors[n].uuid === uuid) {
                            return
                        }
                    }

                    this.authors.push(author)
                    if (cbDone) {
                        cbDone(this.authors)
                    }
                    this.rerender()
                }
            })
    }

    /**
     * Save image news item, called from image archive dialog
     *
     * @param uuid
     * @param xml
     *
     * @private
     */
    saveImageData(uuid, xml) {
        this.context.api.router.put('/api/newsitem/' + uuid, {body: xml, uuid: uuid})
            .then((response) => this.context.api.router.checkForOKStatus(response))
            .then(() => {
                this.context.api.ui.showNotification('', '', this.getLabel('Image data saved to archive'))
            })
            .catch((error) => {
                console.info(error)
                this.context.api.ui.showNotification('', '', this.getLabel('Error! Image data could not be saved.'))
            })
    }
}

export {DialogImageComponent}
